import React, { useMemo, useState } from "react";

import { ROW_HOVER } from "./constants";
import {
  AuditSourceTitle,
  FilterChip,
  PanelHeader,
  ReportSelector,
  ReportTypeIcon,
  RowChevron,
  StatusCounts,
  TwoColumn,
} from "./shared";
import { formatTemplatePath } from "./utils";

export const IssuesPanel = ({
  issuesData,
  loading,
  project,
  reports,
  reportFilter,
  onReportFilter,
  onOpenIssue,
}) => {
  const [statusFilter, setStatusFilter] = useState("all");
  const issues = useMemo(() => {
    const list = issuesData?.issues || [];
    let filtered = list;
    if (statusFilter === "fail") filtered = list.filter((i) => (i.fail_count || 0) > 0);
    else if (statusFilter === "warn") {
      filtered = list.filter((i) => (i.warn_count || 0) > 0 && !(i.fail_count > 0));
    }
    // pages × (2×fails + warns)
    return filtered.slice().sort((a, b) => {
      const aPages = a.page_count || a.pages?.length || 0;
      const bPages = b.page_count || b.pages?.length || 0;
      const aRank = aPages * (2 * (a.fail_count || 0) + (a.warn_count || 0));
      const bRank = bPages * (2 * (b.fail_count || 0) + (b.warn_count || 0));
      return bRank - aRank;
    });
  }, [issuesData, statusFilter]);

  return (
    <TwoColumn
      main={(
        <>
          <PanelHeader
            title={(
              <AuditSourceTitle
                noun="Issues"
                project={project}
                reports={reports}
                reportFilter={reportFilter}
              />
            )}
            count={issues.length}
            filters={(
              <>
                <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")}>All</FilterChip>
                <FilterChip active={statusFilter === "fail"} onClick={() => setStatusFilter("fail")}>Fail</FilterChip>
                <FilterChip active={statusFilter === "warn"} onClick={() => setStatusFilter("warn")}>Warn</FilterChip>
                <ReportSelector
                  reports={reports}
                  value={reportFilter || ""}
                  onChange={onReportFilter}
                />
                {!issuesData?.actionable && (
                  <span className="text-xs text-amber-500">Read-only</span>
                )}
              </>
            )}
          />
          {loading && !issues.length ? (
            <p className="text-sm text-theme-secondary-text">Loading issues…</p>
          ) : (
            <ul className="space-y-0.5 rounded-xl border border-theme-secondary p-1">
              {issues.map((issue) => {
                const preview = [...(issue.pages || [])]
                  .sort((a, b) => (a.status === "fail" ? 0 : 1) - (b.status === "fail" ? 0 : 1))
                  .slice(0, 3)
                  .map((page) => formatTemplatePath(page.site_page_path) || page.site_page_path)
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <li key={issue.key}>
                    <button
                      type="button"
                      onClick={() => onOpenIssue(issue)}
                      className={`flex w-full items-center gap-3 px-3 py-2.5 text-left ${ROW_HOVER}`}
                    >
                      <ReportTypeIcon type={issue.report_type} className="h-6 w-6 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{issue.label}</p>
                        {preview && (
                          <p className="truncate text-xs text-theme-secondary-text">{preview}</p>
                        )}
                      </div>
                      <StatusCounts
                        pages={issue.page_count || issue.pages?.length || 0}
                        counts={{ fail: issue.fail_count, warn: issue.warn_count }}
                        keys={["fail", "warn"]}
                      />
                      <RowChevron />
                    </button>
                  </li>
                );
              })}
              {!issues.length && (
                <li className="px-4 py-8 text-center text-sm text-theme-secondary-text">
                  No issues from the selected reports.
                </li>
              )}
            </ul>
          )}
        </>
      )}
    />
  );
};
